import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export const DownloadPdf = async (mainRef) => {
    try {
        const html2canvas = (await import("html2canvas")).default;
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
        const pdfHeight = pdf.internal.pageSize.getHeight() - 20;
        let currentY = 10;
        let hasContent = false;

        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

        const blocks = mainRef.current.querySelectorAll(".block, .page-break");

        const html2canvasOptions = { scale: 2 };

        const addCanvasToPdf = async (canvas) => {
            const imgData = canvas.toDataURL("image/jpeg", 0.9);
            const imgHeightInPdfUnits = (canvas.height * pdfWidth) / canvas.width;

            if (currentY + imgHeightInPdfUnits > pdfHeight) {
                pdf.addPage();
                currentY = 10;
            }

            pdf.addImage(imgData, "JPEG", 10, currentY, pdfWidth, imgHeightInPdfUnits);
            currentY += imgHeightInPdfUnits + 5;
            hasContent = true;
        };

        const processChild = async (child) => {
            await delay(100);  // Ensure full DOM render

            const chartEl = child.querySelector("canvas");
            let tempImg;

            if (chartEl) {
                const chartInstance = chartEl.__chartjs;
                if (chartInstance) {
                    const base64 = chartInstance.toBase64Image();
                    chartEl.style.display = "none";

                    tempImg = document.createElement("img");
                    tempImg.src = base64;
                    tempImg.style.width = "100%";
                    tempImg.dataset.temp = "true";
                    chartEl.parentNode.appendChild(tempImg);

                    await delay(50);
                }
            }

            const domRect = child.getBoundingClientRect();
            const domHeightPx = domRect.height;
            const domWidthPx = domRect.width;
            const imgHeightInPdfUnits = (domHeightPx * pdfWidth) / domWidthPx;
            const pageHeightPx = (pdfHeight * domWidthPx) / pdfWidth;
            const remainingSpace = pdfHeight - currentY;

            if (child.classList.contains("is-drop-when-no-space")) {
                if (imgHeightInPdfUnits <= remainingSpace) {
                    const canvas = await html2canvas(child, html2canvasOptions);
                    await addCanvasToPdf(canvas);

                } else if (imgHeightInPdfUnits <= pdfHeight) {
                    if (currentY > 10) pdf.addPage();
                    currentY = 10;
                    const canvas = await html2canvas(child, html2canvasOptions);
                    await addCanvasToPdf(canvas);

                } else {
                    let remainingPx = domHeightPx;
                    let sourceY = 0;
                    const sliceHeightPx = pageHeightPx;

                    while (remainingPx > 0) {
                        const canvasSlice = await html2canvas(child, {
                            ...html2canvasOptions,
                            height: Math.min(sliceHeightPx, remainingPx),
                            y: sourceY,
                            width: domWidthPx
                        });

                        await addCanvasToPdf(canvasSlice);

                        sourceY += sliceHeightPx;
                        remainingPx -= sliceHeightPx;
                    }
                }

            } else if (child.classList.contains("fit-or-sliceable")) {
                const nestedChildren = Array.from(child.children);

                for (let nestedChild of nestedChildren) {
                    const nestedRect = nestedChild.getBoundingClientRect();
                    const nestedHeightPx = nestedRect.height;
                    const nestedWidthPx = nestedRect.width;
                    const nestedHeightInPdfUnits = (nestedHeightPx * pdfWidth) / nestedWidthPx;
                    const remainingSpaceNested = pdfHeight - currentY;

                    if (nestedHeightInPdfUnits <= remainingSpaceNested) {
                        const canvas = await html2canvas(nestedChild, html2canvasOptions);
                        await addCanvasToPdf(canvas);

                    } else if (nestedHeightInPdfUnits <= pdfHeight) {
                        if (currentY > 10) pdf.addPage();
                        currentY = 10;
                        const canvas = await html2canvas(nestedChild, html2canvasOptions);
                        await addCanvasToPdf(canvas);

                    } else {
                        let remainingPx = nestedHeightPx;
                        let sourceY = 0;
                        const sliceHeightPx = pageHeightPx;

                        while (remainingPx > 0) {
                            const canvasSlice = await html2canvas(nestedChild, {
                                ...html2canvasOptions,
                                height: Math.min(sliceHeightPx, remainingPx),
                                y: sourceY,
                                width: nestedWidthPx
                            });

                            await addCanvasToPdf(canvasSlice);

                            sourceY += sliceHeightPx;
                            remainingPx -= sliceHeightPx;
                        }
                    }
                }

            } else {
                const canvas = await html2canvas(child, html2canvasOptions);
                await addCanvasToPdf(canvas);
            }

            if (tempImg) {
                chartEl.style.display = "block";
                tempImg.remove();
            }
        };

        for (let block of blocks) {
            const isPageBreak = block.classList.contains("page-break");
            if (isPageBreak) {
                if (currentY > 10) pdf.addPage();
                currentY = 10;
                continue;
            }

            const children = Array.from(block.children);
            for (let child of children) {
                await processChild(child);
            }
        }

        // Remove first page if no content was added and multiple pages exist
        if (!hasContent && pdf.internal.getNumberOfPages() > 1) {
            pdf.deletePage(1);
        }

        pdf.save("report.pdf");
    } catch (err) {
        console.error(err);
    }
};


export const duplicateUserRecords = (data) => {
    const grouped = data.reduce((acc, item) => {
        const key = item.userId;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(item);
        return acc; // ✅ Important: return the accumulator
    }, {});
    let totalDuplicate = 0;
    // Extract only duplicates (userIds with more than 1 record)
    const _data = Object.values(grouped).filter(group => {
        const len = group.length;
        if (len > 1) {
            totalDuplicate += Number(len - 1);
            return group;
        }
    });
    const allList = _data.flatMap((i) => i);
    const duplicate = {
        data: _data,
        allList: allList
    }
    duplicate.total = totalDuplicate;

    duplicate.summedResult = allList.reduce((acc, item) => {
        acc.cnt += parseInt(item.cnt, 10);
        acc.onboardedUser += parseInt(item.onboardedUser, 10);
        acc.is_partial_user += parseInt(item.is_partial_user, 10);
        acc.is_sms_user += parseInt(item.is_sms_user, 10);
        acc.is_other_user += parseInt(item.is_other_user, 10);
        acc.is_whatsapp_user += parseInt(item.is_whatsapp_user, 10);
        return acc;
    }, {
        cnt: 0,
        onboardedUser: 0,
        is_partial_user: 0,
        is_sms_user: 0,
        is_other_user: 0,
        is_whatsapp_user: 0
    });

    return duplicate;
};


export const groupDivisionProgramSummery = (referralData) => {
    referralData = referralData.data || [];
    // console.log(referralData);
    const areaMap = {};
    const countData = {
        onboardedUser: 0,
        sms: 0,
        whatsapp: 0,
        other: 0,
        partial_user: 0,
        total: 0
    };

    referralData.forEach(({ areaId, areaName, programId, name, onboardedUser, is_sms_user, is_whatsapp_user, is_other_user, is_partial_user, is_account_created }) => {
        if (!areaMap[areaId]) {
            areaMap[areaId] = { areaId, areaName, programs: {} };
        }
        const programs = areaMap[areaId].programs;
        programs[programId] = programs[programId] || { programId, name, count: 0 };
        programs[programId].count += Number(is_account_created);
        countData.onboardedUser += Number(onboardedUser);
        countData.sms += Number(is_sms_user);
        countData.whatsapp += Number(is_whatsapp_user);
        countData.other += Number(is_other_user);
        countData.total += Number(is_account_created);
        countData.partial_user += Number(is_partial_user);
    });
    countData.onboardedUser = formatUkNumber(countData.onboardedUser);

    return {
        ...countData,
        data: Object.values(areaMap).map(({ areaId, areaName, programs }) => ({
            areaId,
            areaName,
            programs: Object.values(programs).map((item) => {
                item.count = formatUkNumber(item.count);
                return item;
            })
        }))
    };
}

export const groupAreasWithClinics = (data) => {
    const areaMap = {};
    data = data.areaTableData || [];
    data.forEach(({ areaId, areaName, clinicId, clinicName, programId, name: programName, is_sms_user, is_whatsapp_user, is_other_user, onboardedUser, is_partial_user }) => {
        if (!areaMap[areaId]) {
            areaMap[areaId] = { areaId, areaName, clinics: {}, totalClinics: 0 };
        }

        const area = areaMap[areaId];
        area.totalClinics += 1;



        const clinics = area.clinics;
        // const normalizedClinicName = clinicName.trim() || "none";
        const normalizedClinicName = (clinicName || "").trim() || "none";

        if (!clinics[clinicId]) {
            clinics[clinicId] = {
                clinicId,
                clinicName: normalizedClinicName,
                count: 0,
                programs: {}
            };
        }

        clinics[clinicId].count += 1;


        const programs = clinics[clinicId].programs;

        if (!programs[programId]) {
            programs[programId] = {
                programId, programName, count: 0,
                sms: 0,
                whatsapp: 0,
                other: 0,
                onboard: 0,
                partial_user: 0,
            };
        }

        programs[programId].count += 1;
        // Sum the user type counts
        programs[programId].sms += Number(is_sms_user);
        programs[programId].whatsapp += Number(is_whatsapp_user);
        programs[programId].other += Number(is_other_user);
        programs[programId].onboard += Number(onboardedUser);
        programs[programId].partial_user += Number(is_partial_user);
    });

    return Object.values(areaMap).map(({ areaId, areaName, clinics, totalClinics }) => ({
        areaId,
        areaName,
        totalClinics: formatUkNumber(totalClinics),
        clinics: Object.values(clinics).map(({ clinicId, clinicName, count, programs }) => ({
            clinicId,
            clinicName,
            count: formatUkNumber(count),
            programs: Object.values(programs).map((item) => {
                item.sms = formatUkNumber(item.sms);
                item.whatsapp = formatUkNumber(item.whatsapp);
                item.other = formatUkNumber(item.other);
                item.onboard = formatUkNumber(item.onboard);
                item.partial_user = formatUkNumber(item.partial_user);
                return item;
            })
        }))
    }));
}

export const groupByClinics = (data) => {
    const clinicMap = {};

    data.forEach(({ clinicName, is_sms_user, is_whatsapp_user, is_other_user, onboardedUser, is_partial_user, is_account_created }) => {
        const normalizedClinicName = clinicName.trim() || "None";

        clinicMap[normalizedClinicName] = clinicMap[normalizedClinicName] || {
            clinicName: normalizedClinicName, count: 0, sms: 0,
            whatsapp: 0,
            other: 0,
            onboard: 0,
            partial_user: 0
        };
        clinicMap[normalizedClinicName].count += Number(is_account_created);
        clinicMap[normalizedClinicName].sms += Number(is_sms_user);
        clinicMap[normalizedClinicName].whatsapp += Number(is_whatsapp_user);
        clinicMap[normalizedClinicName].other += Number(is_other_user);
        clinicMap[normalizedClinicName].onboard += Number(onboardedUser);
        clinicMap[normalizedClinicName].partial_user += Number(is_partial_user);
    });

    return Object.values(clinicMap).map(item => {
        item.onboard = formatUkNumber(item.onboard);
        item.count = formatUkNumber(item.count);
        item.sms = formatUkNumber(item.sms);
        item.whatsapp = formatUkNumber(item.whatsapp);
        item.other = formatUkNumber(item.other);
        return item;
    });
}

export const groupReferralData = (data) => {
    const programMap = {};
    const clinicMap = {};
    const areaMap = {};

    data.forEach((item) => {
        const {
            areaId,
            areaName,
            programId,
            name: programName,
            clinicId,
            clinicName,
            cnt,
            onboardedUser,
            onboardedUser2,
            inclinic,
            is_partial_user,
            is_sms_user,
            is_other_user,
            is_whatsapp_user
        } = item;

        // Helper to parse number safely
        const toNum = (val) => Number(val) || 0;

        // --- Group by Program ---
        const programKey = programId;
        if (!programMap[programKey]) {
            programMap[programKey] = {
                programId,
                programName: programName || "None",
                cnt: 0,
                onboardedUser: 0,
                onboardedUser2: 0,
                inclinic: 0,
                is_partial_user: 0,
                is_sms_user: 0,
                is_other_user: 0,
                is_whatsapp_user: 0
            };
        }

        const programGroup = programMap[programKey];
        programGroup.cnt += toNum(cnt);
        programGroup.onboardedUser += toNum(onboardedUser);
        programGroup.onboardedUser2 += toNum(onboardedUser2);
        programGroup.inclinic += toNum(inclinic);
        programGroup.is_partial_user += toNum(is_partial_user);
        programGroup.is_sms_user += toNum(is_sms_user);
        programGroup.is_other_user += toNum(is_other_user);
        programGroup.is_whatsapp_user += toNum(is_whatsapp_user);

        // --- Group by Clinic Name ---
        const clinicKey = clinicId;
        if (!clinicMap[clinicKey]) {
            clinicMap[clinicKey] = {
                clinicId,
                clinicName: clinicName || "None",
                cnt: 0,
                onboardedUser: 0,
                onboardedUser2: 0,
                inclinic: 0,
                is_partial_user: 0,
                is_sms_user: 0,
                is_other_user: 0,
                is_whatsapp_user: 0
            };
        }

        const clinicGroup = clinicMap[clinicKey];
        clinicGroup.cnt += toNum(cnt);
        clinicGroup.onboardedUser += toNum(onboardedUser);
        clinicGroup.onboardedUser2 += toNum(onboardedUser2);
        clinicGroup.inclinic += toNum(inclinic);
        clinicGroup.is_partial_user += toNum(is_partial_user);
        clinicGroup.is_sms_user += toNum(is_sms_user);
        clinicGroup.is_other_user += toNum(is_other_user);
        clinicGroup.is_whatsapp_user += toNum(is_whatsapp_user);

        // --- Group by Area Name ---
        const areaKey = areaId;
        if (!areaMap[areaKey]) {
            areaMap[areaKey] = {
                areaId,
                areaName: areaName || "None",
                cnt: 0,
                onboardedUser: 0,
                onboardedUser2: 0,
                inclinic: 0,
                is_partial_user: 0,
                is_sms_user: 0,
                is_other_user: 0,
                is_whatsapp_user: 0
            };
        }
        const areaGroup = areaMap[areaKey];
        areaGroup.cnt += toNum(cnt);
        areaGroup.onboardedUser += toNum(onboardedUser);
        areaGroup.onboardedUser2 += toNum(onboardedUser2);
        areaGroup.inclinic += toNum(inclinic);
        areaGroup.is_partial_user += toNum(is_partial_user);
        areaGroup.is_sms_user += toNum(is_sms_user);
        areaGroup.is_other_user += toNum(is_other_user);
        areaGroup.is_whatsapp_user += toNum(is_whatsapp_user);
    });


    return {
        byProgram: generateChartData(Object.values(programMap), 'programName'),
        byClinic: generateChartData(Object.values(clinicMap), 'clinicName'),
        byArea: generateChartData(Object.values(areaMap), 'areaName'),
    };
};

const generateChartData = (groupArray, labelKey) => {
    let totalData = {
        total: 0,
        active: 0,
        inactive: 0
    };
    return {
        labels: groupArray.map(item => item[labelKey]), // E.g., clinicName, programName, areaName
        datasets: [
            {
                label: "App Downloaded",
                data: groupArray.map(item => {
                    totalData.total += item.onboardedUser2;
                    return formatUkNumber(item.onboardedUser2);
                }),
                backgroundColor: "#007bff",
            },
            {
                label: "Active",
                data: groupArray.map(item => {
                    totalData.active += item.onboardedUser;
                    return formatUkNumber(item.onboardedUser);
                }),
                backgroundColor: "#ffc107",
            },
            {
                label: "Inactive",
                data: groupArray.map(item => {
                    const _value = (item.cnt - (item.onboardedUser2 + item.is_partial_user));
                    totalData.inactive += _value;
                    return formatUkNumber(_value);
                }),
                backgroundColor: "#28a745",
            }
        ],
        totalData: {
            total: formatUkNumber(totalData.total),
            active: formatUkNumber(totalData.active),
            inactive: formatUkNumber(totalData.inactive),
            percentage: percentageActive(totalData?.total, totalData?.active)
        }
    };
};

export const getFromToDefaultValue = () => {
    const today = new Date();

    // Calculate 1 month ago
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);

    // Format to yyyy-mm-dd
    const _formatDate = (date) => {
        const d = new Date(date);
        const month = `${d.getMonth() + 1}`.padStart(2, '0');
        const day = `${d.getDate()}`.padStart(2, '0');
        const year = d.getFullYear();
        return `${year}-${month}-${day}`;
    };

    return {
        fromDate: _formatDate(oneMonthAgo),
        toDate: _formatDate(today)  // Or any logic you want
    };
}


export const groupAreaDivisionProgramSummary = (referralData) => {
    const areaMap = {};
    const clinicMap = {};
    const programMap = {};
    const totalCounts = { onboardedUser: 0, active: 0, partialUser: 0, inactive: 0, total: 0 };

    // Track unique active users globally and per group
    const uniqueActiveUserIds = new Set();
    const programActiveUserIds = {};
    const clinicActiveUserIds = {};
    const areaActiveUserIds = {};

    referralData.forEach(item => {
        const {
            userId,
            areaId, areaName,
            clinicId, clinicName,
            programId, name,
            onboardedUser, active_user, is_partial_user, cnt2,is_account_created2
        } = item;
        const onboarded = Number(onboardedUser || 0);
        const partial = Number(is_partial_user || 0);
        const totalCnt = Number(cnt2 || 0);
        const act = Number(active_user || 0);
        const total = Number(is_account_created2||0);
        const inactive = totalCnt - (onboarded + partial);

        totalCounts.onboardedUser += onboarded;
        totalCounts.partialUser += partial;
        totalCounts.inactive += inactive;
        totalCounts.total += total;
        totalCounts.active += act;
        // // === Handle unique active user globally ===
        // if (act > 0 && userId && !uniqueActiveUserIds.has(userId)) {
        //     totalCounts.active += 1;
        //     uniqueActiveUserIds.add(userId);
        // }

        // --- PROGRAM ---
        const progName = name?.trim() || "None";
        if (!programMap[programId]) programMap[programId] = { programId, name: progName, onboardedUser: 0, active: 0, partialUser: 0, inactive: 0, total: 0 };
        if (!programActiveUserIds[programId]) programActiveUserIds[programId] = new Set();

        programMap[programId].onboardedUser += onboarded;
        programMap[programId].partialUser += partial;
        programMap[programId].inactive += inactive;
        programMap[programId].total += total;

        if (act > 0 && userId && !programActiveUserIds[programId].has(userId)) {
            programMap[programId].active += 1;
            programActiveUserIds[programId].add(userId);
        }

        // --- CLINIC ---
        const clName = clinicName?.trim() || "None";
        if (!clinicMap[clinicId]) clinicMap[clinicId] = { clinicId, clinicName: clName, onboardedUser: 0, active: 0, partialUser: 0, inactive: 0, total: 0 };
        if (!clinicActiveUserIds[programId]) clinicActiveUserIds[programId] = new Set();

        clinicMap[clinicId].onboardedUser += onboarded;
        clinicMap[clinicId].partialUser += partial;
        clinicMap[clinicId].inactive += inactive;
        clinicMap[clinicId].total += total;
        clinicMap[clinicId].active += act;
        // if (act > 0 && userId && !clinicActiveUserIds[programId].has(userId)) {
        //     clinicMap[clinicId].active += 1;
        //     clinicActiveUserIds[programId].add(userId);
        // }

        // --- AREA ---
        const arName = areaName?.trim() || "None";
        if (!areaMap[areaId]) areaMap[areaId] = { areaId, areaName: arName, onboardedUser: 0, active: 0, partialUser: 0, inactive: 0, total: 0 };
        if (!areaActiveUserIds[areaId]) areaActiveUserIds[areaId] = new Set();

        areaMap[areaId].onboardedUser += onboarded;
        areaMap[areaId].partialUser += partial;
        areaMap[areaId].inactive += inactive;
        areaMap[areaId].total += total;
        areaMap[areaId].active += act;
        // if (act > 0 && userId && !areaActiveUserIds[areaId].has(userId)) {
        //     areaMap[areaId].active += 1;
        //     areaActiveUserIds[areaId].add(userId);
        // }
    });

    const formatCounts = (obj) => ({
        ...obj,
        onboardedUser: formatUkNumber(obj.onboardedUser),
        total: formatUkNumber(obj.total),
        active: formatUkNumber(obj.active),
        partialUser: formatUkNumber(obj.partialUser),
        inactive: formatUkNumber(obj.onboardedUser - obj.active),
        percentage: percentageActive(obj.onboardedUser, obj.active),
    });

    return {
        total: formatCounts(totalCounts),
        programWise: Object.values(programMap).map(formatCounts),
        clinicWise: Object.values(clinicMap).map(formatCounts),
        areaWise: Object.values(areaMap).map(formatCounts),
    };
};


export const formatDate = (dateStr) => {
    if (dateStr == '') {
        return dateStr;
    }
    const date = new Date(dateStr);

    const day = date.getDate();
    const year = date.getFullYear();

    const monthName = date.toLocaleString('default', { month: 'short' });

    // Get ordinal suffix (st, nd, rd, th)
    const getOrdinal = (n) => {
        if (n >= 11 && n <= 13) return 'th';
        switch (n % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    return `${monthName} ${day}${getOrdinal(day)}, ${year}`;
};


export function getCache() {
    const cache = localStorage.getItem("cacheHistory");
    return cache ? JSON.parse(cache) : [];
}

// export Function to add an item to cache without duplicates
export function addToCache(newItem) {
    let cache = getCache();

    // check for duplicates ignoring timestamp
    const exists = cache.some(item => {
        const { timestamp: _, ...restItem } = item;   // remove timestamp
        return JSON.stringify(restItem) === JSON.stringify(newItem);
    });

    if (!exists) {
        // add timestamp when storing
        cache.push({
            ...newItem,
            timestamp: formatDate(new Date())
        });

        localStorage.setItem("cacheHistory", JSON.stringify(cache));
    }
}

// export Function to clear cache if needed
export function clearCache() {
    localStorage.removeItem("cacheHistory");
}


export const formatUkNumber = (value) => {
    // Accept number or string (trim whitespace)
    const s = String(value).trim();
    if (s === '' || s.toLowerCase() === 'nan') return '';
    // handle sign
    const sign = s[0] === '-' ? '-' : '';
    const unsigned = s.replace(/^-/, '');
    // split integer and decimal parts (keeps everything after first dot as decimal)
    const [intPart, ...decParts] = unsigned.split('.');
    const decPart = decParts.length ? decParts.join('.') : undefined;
    // insert commas into integer part (works for arbitrarily long ints)
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decPart !== undefined ? `${sign}${intFormatted}.${decPart}` : `${sign}${intFormatted}`;
}



export const generatePDF = async (contentRef, fileName = "referral-report", imageUrl = null, bgColor = "#ffffff") => {
    try {
        const html2canvas = (await import("html2canvas")).default;
        const pdf = new jsPDF("p", "mm", "a4");

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const contentWidth = pageWidth - margin * 2;
        let yPosition = margin;

        // Hex → RGB
        function hexToRgb(hex) {
            const bigint = parseInt(hex.replace("#", ""), 16);
            return {
                r: (bigint >> 16) & 255,
                g: (bigint >> 8) & 255,
                b: bigint & 255,
            };
        }

        // Load image to base64
        async function loadImageToBase64(url) {
            return new Promise((resolve, reject) => {
                if (!url) return resolve(null);
                const img = new Image();
                img.crossOrigin = "anonymous";
                img.src = url;
                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL("image/png"));
                };
                img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
            });
        }

        // Set PDF background (color + optional tiled image)
        function setPdfBackground(pdf, hex, base64Image = null) {
            if (hex) {
                const { r, g, b } = hexToRgb(hex);
                pdf.setFillColor(r, g, b);
                pdf.rect(0, 0, pageWidth, pageHeight, "F");
            }

            if (base64Image) {
                const tileWidth = 100;
                const tileHeight = 150;
                for (let y = 0; y < pageHeight; y += tileHeight) {
                    for (let x = 0; x < pageWidth; x += tileWidth) {
                        pdf.addImage(base64Image, "PNG", x, y, tileWidth, tileHeight);
                    }
                }
            }
        }

        // Preload background image
        const bgImage = await loadImageToBase64(imageUrl);

        // First page background
        setPdfBackground(pdf, bgColor, bgImage);

        // Render each .pdf-element
        const elements = contentRef.current.querySelectorAll(".pdf-element");

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];

            const canvas = await html2canvas(element, {
                scale: 0.9,
                useCORS: true,
                backgroundColor: null, // transparent to show PDF bg
            });

            const imgData = canvas.toDataURL("image/png");
            const imgHeight = (canvas.height * contentWidth) / canvas.width;

            if (imgHeight <= pageHeight - margin * 2) {
                // fits on current page
                if (yPosition + imgHeight > pageHeight - margin) {
                    pdf.addPage();
                    setPdfBackground(pdf, bgColor, bgImage);
                    yPosition = margin;
                }
                pdf.addImage(imgData, "PNG", margin, yPosition, contentWidth, imgHeight);
                yPosition += imgHeight + 15;
            } else {
                // slice large element across pages
                const pageCanvasHeight = (pageHeight - margin * 2) * (canvas.width / contentWidth);
                let position = 0;
                let firstSlice = true;

                while (position < canvas.height) {
                    const sliceCanvas = document.createElement("canvas");
                    sliceCanvas.width = canvas.width;
                    sliceCanvas.height = Math.min(pageCanvasHeight, canvas.height - position);

                    const ctx = sliceCanvas.getContext("2d");
                    ctx.drawImage(
                        canvas,
                        0,
                        position,
                        canvas.width,
                        sliceCanvas.height,
                        0,
                        0,
                        canvas.width,
                        sliceCanvas.height
                    );

                    const sliceImgData = sliceCanvas.toDataURL("image/png");
                    const sliceImgHeight = (sliceCanvas.height * contentWidth) / sliceCanvas.width;

                    if (!firstSlice) {
                        pdf.addPage();
                        setPdfBackground(pdf, bgColor, bgImage);
                        yPosition = margin;
                    }

                    pdf.addImage(sliceImgData, "PNG", margin, yPosition, contentWidth, sliceImgHeight);

                    position += sliceCanvas.height;
                    yPosition = margin + sliceImgHeight + 15;
                    firstSlice = false;
                }
            }
        }
        pdf.save(`${fileName}.pdf`);
    } catch (error) {
        console.error("Error generating PDF:", error);
        alert("Error generating PDF. Please try again.");
    }
};


export function getFinancialDateRange(dates) {
    if (!dates?.start) {
        return "";
    }
    return `${formatDate(dates.start)} - ${formatDate(dates.end)}`;
}


// ---- Status Box ----
const addStatusBox = (doc, x, y, title, count, color = "#d8cdfb", boxWidth = 40) => {
    const boxHeight = 15;

    // Box
    doc.setFillColor(color);
    doc.roundedRect(x, y, boxWidth, boxHeight, 3, 3, "F");

    // Title
    doc.setFontSize(11);
    doc.setTextColor("#353535");
    doc.text(title, x + boxWidth / 2, y + 6, { align: "center" });

    // Count
    doc.setFontSize(10);
    doc.text(String(count), x + boxWidth / 2, y + 12, { align: "center" });
};

const makeThreeTable = (doc, startY = 20, coachingInsight, formData) => {
    const date = getFinancialDateRange(formData.financialYearRange);
    const weekly = `${formData._fromDate} - ${formData._toDate}`;


    let currentY = startY;
    currentY = addHeading(doc, {
        title: "Overall Coaching Insights",
        subtitle: `Date Range : ${weekly} \n Yearly : ${date}`, // optional                           
        boxWidth: 38,
        boxHeight: 18,
    }, currentY);

    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text("Call Insights", 25, currentY);

    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text("Messaging Insights", 80, currentY);

    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text("Rating Insights", 150, currentY);

    const pageWidth = doc.internal.pageSize.getWidth();
    const gap = 5; // gap between tables
    const tableWidth = (pageWidth - 4 * gap) / 3; // divide page into 3 equal parts

    // Column widths
    const callCols = [tableWidth * 0.4, tableWidth * 0.6];
    const messageCols = [tableWidth * 0.3, tableWidth * 0.35, tableWidth * 0.35];
    const ratingCols = [tableWidth * 0.3, tableWidth * 0.4, tableWidth * 0.3];

    currentY += 5;
    const { ratingData, messagesData, callData } = coachingInsight;
    // Date Range : {weekly}<br/>
    //                 Yearly : {date}
    autoTable(doc, {
        startY: currentY,
        head: [[
            { content: "Period", styles: { halign: "center" }, cellWidth: callCols[0] },
            { content: "Total Calls", styles: { halign: "center" }, cellWidth: callCols[1] },

            makeGapColumn(gap), // first gap column

            { content: "Period", styles: { halign: "center" }, cellWidth: messageCols[0] },
            { content: "Messages\nSent by Coach", styles: { halign: "center" }, cellWidth: messageCols[1] },
            { content: "Messages\nSent by Patient", styles: { halign: "center" }, cellWidth: messageCols[2] },

            makeGapColumn(gap), // second gap column

            { content: "Period", styles: { halign: "center" }, cellWidth: ratingCols[0] },
            { content: "Total Ratings\nReceive", styles: { halign: "center" }, cellWidth: ratingCols[1] },
            { content: "Average\nRating", styles: { halign: "center" }, cellWidth: ratingCols[2] },
        ]],
        body: [
            [
                `Date Range`,
                formatUkNumber(callData?.last_full_week_conversations || 0),
                "", // gap handled by columnStyles below
                `Date Range`,
                formatUkNumber(messagesData?.latest_week_coach_messages || 0),
                formatUkNumber(messagesData?.latest_week_user_messages || 0),
                "",
                `Date Range`,
                formatUkNumber(ratingData?.weekly_rating_count || 0),
                formatUkNumber(ratingData?.weekly_avg_rating || 0)
            ],
            [
                `Yearly`,
                formatUkNumber(callData?.total_conversations || 0),
                "",
                `Yearly`,
                formatUkNumber(messagesData?.total_coach_messages || 0),
                formatUkNumber(messagesData?.total_user_messages || 0),
                "",
                `Yearly`,
                formatUkNumber(ratingData?.overall_rating_count || 0),
                formatUkNumber(ratingData?.overall_avg_rating || 0)
            ]
        ],
        theme: "grid",
        styles: {
            fontSize: 9,
            cellPadding: 3,
            lineColor: "#555",
            lineWidth: 0.5
        },
        columnStyles: {
            2: { fillColor: 0, lineWidth: { top: 0, bottom: 0, left: 0.5, right: 0.5 }, lineColor: { left: "#555", right: "#555" } },
            6: { fillColor: 0, lineWidth: { top: 0, bottom: 0, left: 0.5, right: 0.5 }, lineColor: { left: "#555", right: "#555" } }
        },
        headStyles: {
            fillColor: "#baf2d2",
            textColor: "black",
            lineColor: "#555",
            lineWidth: 0.5
        },
    });

    return currentY;
};
// ---- Heading ----
const addHeading = (
    doc,
    { title = null, subtitle = "", subtitleColor = "#888888", leftText = "", statuses = [], leftTextColor = 0, text = null, boxWidth = 32, boxHeight = 12 },
    startY = 20,
) => {
    let currentY = startY;

    if (title) {
        const pageWidth = doc.internal.pageSize.getWidth();
        const marginX = 20;
        const padding = 5;

        doc.setFontSize(18);
        const titleWidth = doc.getTextWidth(title);
        let boxWidth = titleWidth + padding * 2;

        if (subtitle && subtitle.trim() !== "") {
            doc.setFontSize(10);
            const subtitleWidth = doc.getTextWidth(subtitle);
            boxWidth = Math.max(boxWidth, subtitleWidth + padding * 2);
        }

        const boxX = (pageWidth - boxWidth) / 2;
        const boxY = currentY;
        let _boxHeight = boxHeight; // for title line
        if (subtitle && subtitle.trim() !== "") _boxHeight += 8;

        // Draw white rounded box
        doc.setDrawColor(200); // light border
        doc.setFillColor(255, 255, 255); // white background
        doc.roundedRect(boxX, boxY, boxWidth, _boxHeight, 3, 3, "FD"); // Fill + Draw

        // Add Title
        doc.setFontSize(18);
        doc.setTextColor(40);
        doc.text(title, pageWidth / 2, boxY + 8, { align: "center" });

        // Add Subtitle
        if (subtitle && subtitle.trim() !== "") {
            doc.setFontSize(10);
            doc.setTextColor(subtitleColor || 80);
            doc.text(subtitle, pageWidth / 2, boxY + 16, { align: "center" });
        }

        currentY = boxY + _boxHeight + 6; // move cursor after box
    }

    // Subtitle (optional)
    if (text && text.trim() !== "") {
        doc.setFontSize(8);
        doc.setTextColor("#525f7f");
        const pageWidth = doc.internal.pageSize.getWidth();
        const maxWidth = pageWidth - 28; // 14mm margin on left & right

        // Split text into lines that fit within maxWidth
        const lines = doc.splitTextToSize(text, maxWidth);

        // Draw each line centered
        lines.forEach((line) => {
            currentY += 5; // spacing between lines
            doc.text(line, pageWidth / 2, currentY, { align: "center" });
        });

    } else {
        currentY += 10;
    }


    // Left-aligned text (optional)
    if (leftText && leftText.trim() !== "") {
        doc.setFontSize(12);
        doc.setTextColor(leftTextColor);
        doc.text(leftText, 14, currentY + 8);
    }

    // Status boxes (optional)
    if (Array.isArray(statuses) && statuses.length > 0) {

        const gap = 5;
        const startYBoxes = currentY;
        const totalWidth = statuses.length * boxWidth + (statuses.length - 1) * gap;
        let startX = doc.internal.pageSize.getWidth() - 14 - totalWidth;

        statuses.forEach((s) => {
            addStatusBox(doc, startX, startYBoxes, s.title, s.count, s.color, boxWidth);
            startX += boxWidth + gap;
        });

        currentY += 20; // spacing after boxes
    } else {
        currentY += 10; // spacing after heading if no statuses
    }

    return currentY; // Y position after heading + subtitle + boxes
};


const addBackgroundColor = (doc, color = "#f5f5f5") => {
    doc.setFillColor(color);
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight(), "F");
};

const addHeader = (doc, leftLogo, rightLogo, headerY = 5, logoHeight = 15) => {
    const pageWidth = doc.internal.pageSize.getWidth();

    if (leftLogo) {
        doc.addImage(leftLogo, "PNG", 10, headerY - 8, 30, 0); // left side
    }
    if (rightLogo) {
        doc.addImage(rightLogo, "PNG", pageWidth - 40, headerY, 30, 0); // right side
    }
};

// ---- Table ----
const addTable = (doc, tableData, startY = 50, options = {}) => {
    const headColor = options.headColor || [255, 0, 0];
    const textColor = options.headTextColor || "#0978be";
    const lineWidth = options.lineWidth !== undefined ? options.lineWidth : 0.2;
    // const bgColor = options.bgColor || "#f7fafe";

    // Track the starting page number
    // const startingPageNumber = doc.internal.getCurrentPageInfo().pageNumber;
    // const paintedPages = new Set();

    autoTable(doc, {
        startY,
        head: tableData.head,
        body: tableData.body,
        margin: { top: 30 },

        styles: {
            halign: "center",
            valign: "middle",
            fillColor: [255, 255, 255],
            lineWidth,
            lineColor: "#555",
        },
        headStyles: {
            fillColor: headColor,
            textColor: textColor,
            lineWidth,
            lineColor: "#555",
        },

        // didAddPage: (data) => {
        //     const currentPageNumber = doc.internal.getCurrentPageInfo().pageNumber;
        //     if (!paintedPages.has(currentPageNumber)) {
        //         addBackgroundColor(doc, bgColor);
        //         if (options.leftLogo || options.rightLogo) {
        //             addHeader(doc, options.leftLogo, options.rightLogo);
        //         }
        //         paintedPages.add(currentPageNumber);
        //     }
        // },

        // willDrawCell: (data) => {
        //     const currentPageNumber = data.pageNumber || doc.internal.getCurrentPageInfo().pageNumber;
        //     // Paint only once per page and skip the starting page (already painted outside)
        //     if (currentPageNumber > startingPageNumber && !paintedPages.has(currentPageNumber)) {
        //         addBackgroundColor(doc, bgColor);
        //         if (options.leftLogo || options.rightLogo) {
        //             addHeader(doc, options.leftLogo, options.rightLogo);
        //         }
        //         paintedPages.add(currentPageNumber);
        //     }
        // },

        alternateRowStyles: { fillColor: [255, 255, 255] },
    });

    return doc.lastAutoTable.finalY + 10; // return Y after table
};


// Function to load image as base64
const loadImageAsBase64 = (url) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous"; // allow loading from public
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = reject;
        img.src = url;
    });
};

// Add background image to a page
const addBackgroundImage = (doc, imgData) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);
};


const makeReffralTableData = (referralData, formData) => {

    // const divisonTableData = groupDivisionProgramSummery(referralData);
    // const areaTableData = groupAreasWithClinics(referralData);
    const divisonTableData = referralData.divisonTableData;
    const areaTableData = referralData.clinicData.areaTableData;
    const bodyData = [];
    divisonTableData.data.map((i) => {
        let length = i.programs.length;
        i.programs.map((p, index) => {
            if (index == 0) {
                bodyData.push([
                    { content: i.areaName, rowSpan: length, styles: { valign: "middle" } },
                    p.count,
                    p.name,
                ]);
            } else {
                bodyData.push([
                    p.count,
                    p.name,
                ]);
            }
        })

    })

    const body2 = [];

    areaTableData.map((item, index) => {
        const clinicData = [];
        item.clinics.map((val) => {
            let length = val.programs.length;
            val.programs.map((p, index) => {
                if (index == 0) {
                    clinicData.push([
                        { content: val.clinicName, rowSpan: length, styles: { valign: "middle" } },
                        p.programName,
                        p.count,
                        p.onboard,
                        p.sms,
                        p.whatsapp,
                        p.other
                    ]);
                } else {
                    clinicData.push([
                        p.programName,
                        p.count,
                        p.onboard,
                        p.sms,
                        p.whatsapp,
                        p.other
                    ]);
                }
                return val;
            })
            return val;
        })

        body2.push({
            heading: {
                title: index == 0 ? "Practice-level & Source Split" : null,
                leftText: item.areaName,
                leftTextColor: '#53b73e',
                statuses: [{ title: "Total Patients", count: item.totalClinics, color: "#d6f0fb" }],
                boxWidth: 38,
            },
            tableData: {
                head: [["Practice", "Programme", "Total", "Total Onboarded", "SMS", "WhatsApp", "Other"]],
                body: clinicData,
            },
            headColor: "#e4ecf8"
        });
        return item;
    })


    return [
        {
            heading: {
                title: "Onboarded Report",
                subtitle: `${formData._fromDate} - ${formData._toDate}`, // optional
                subtitleColor: "#ff9d19",
                leftTextColor: '#53b73e',
                leftText: "Division & Programme summary",   // optional
                statuses: [
                    { title: "Total Onboarded", count: divisonTableData.onboardedUser, color: "#e0faff" },
                    { title: "Account Created", count: divisonTableData.total, color: "#d8cdfb" },
                ], // optional
                boxWidth: 38,
            },
            // Division	Account Created	Programe
            tableData: {
                head: [["Division", "Account Created", "Programme"]],
                body: bodyData,
            },
            headColor: "#e4ecf8",
        },
        ...body2
    ];

}
const percentageActive = (total, active) => {
    if (total === 0) return (0).toFixed(2); // Avoid division by zero
    return ((active / total) * 100).toFixed(2);
};
const makeDivisonTableData = (referralData, formData) => {

    const date = getFinancialDateRange(formData.financialYearRange);
    const bodyData = [];
    // const clinicsData = groupByClinics(referralData);
    const clinicsData = referralData.clinicData.clinicsData;

    clinicsData.map((item) => {
        bodyData.push([
            item.clinicName,
            item.onboard || 0,
            item.count || 0,
            item.sms || 0,
            item.whatsapp || 0,
            item.other || 0
        ])
        return item;
    })


    // const { areaWise, clinicWise, programWise, total } = groupAreaDivisionProgramSummary(referralData);
    const programWise = referralData.clinicData.divisions.programWise;
    const clinicWise = referralData.clinicData.divisions.clinicWise;
    const total = referralData.clinicData.divisions.total;
    const areaWise = referralData.clinicData.divisions.areaWise;

    const programTableData = [];
    const clinicTableData = [];


    programWise.map((val, index) => {
        programTableData.push([
            val.name,
            val.total,
            val.onboardedUser,
            val.active,
            val.inactive,
        ])
    })

    clinicWise.map((val, index) => {
        clinicTableData.push([
            val.clinicName,
            val.total,
            val.onboardedUser,
            val.active,
            val.inactive,
        ])
    })


    return [
        {
            heading: {
                title: "Divisional Report",
                subtitle: `Practice-Wise Account Performance: ${formData?.division?.label ?? "All"} \n  Date Range : ${formData._fromDate} - ${formData._toDate}`, // optional
                boxHeight: 18,
                text: `Breakdown of accounts created and referred for each practice, both year-to-date (${date}) and for the selected ( ${formData._fromDate} -  ${formData._toDate} ), along with a yearly source-wise split (SMS,WhatsApp, Others)`,
                subtitleColor: "#525f7f",
                leftTextColor: '#53b73e',
            },
            // Division	Account Created	Programe
            tableData: {
                head: [
                    [
                        { content: "Period", styles: { halign: "center", valign: "middle" } },
                        { content: `${formData._fromDate} - ${formData._toDate}`, styles: { halign: "center" } },
                        { content: date, colSpan: 4, styles: { halign: "center", valign: "middle" } }

                    ],
                    [
                        { content: "Practice Name", styles: { halign: "center" } },
                        { content: "Total Onboarded", styles: { halign: "center" } },
                        { content: "Account Created", styles: { halign: "center" } },
                        { content: "Referred Source (SMS)", styles: { halign: "center" } },
                        { content: "Referred Source (WhatsApp)", styles: { halign: "center" } },
                        { content: "Referred Source (Others)", styles: { halign: "center" } },
                    ],
                ],
                body: bodyData,
                headStyles: {
                    fillColor: [186, 242, 210], // light green like screenshot
                    textColor: [0, 0, 0],
                    halign: "center",
                    valign: "middle",
                },
                styles: {
                    halign: "center",
                    valign: "middle",
                },
            },
            headColor: "#baf2d2",
        },
        {
            isThreeTable: true
        },
        {
            heading: {

                leftTextColor: '#53b73e',
                leftText: "Division & program summary",   // optional
                statuses: [
                    { title: "Referred", count: total?.total || 0, color: "#fff2cb" },
                    { title: "Onboarded", count: total?.onboardedUser || 0, color: "#d6f0fb" },
                    { title: "Active", count: total?.active || 0, color: "#d8cdfb" },
                    { title: "Inactive", count: total?.inactive || 0, color: "#e0fbd6" },
                    { title: "% Active", count: total?.percentage, color: "#fbd7d6" },
                ], // optional
                boxWidth: 20,
            },
            // Division	Account Created	Programe
            tableData: {
                head: [
                    [
                        { content: "Programme", styles: { halign: "center", valign: "middle" } },
                        { content: "Referred", styles: { halign: "center", valign: "middle" } },
                        { content: "Onboarded", styles: { halign: "center", valign: "middle" } },
                        { content: "Active", styles: { halign: "center", valign: "middle" } },
                        { content: "Inactive", styles: { halign: "center", valign: "middle" } },
                    ],
                ],
                body: programTableData,
                headStyles: {
                    fillColor: [186, 242, 210], // light green like screenshot
                    textColor: "black",
                    halign: "center",
                    valign: "middle",
                },
                styles: {
                    halign: "center",
                    valign: "middle",
                },
            },
            headColor: "#baf2d2",
        },
        {
            heading: {

                leftTextColor: '#53b73e',
                leftText: "Practice summary",   // optional
                statuses: [
                    { title: "Referred", count: total?.total || 0, color: "#fff2cb" },
                    { title: "Onboarded", count: total?.onboardedUser || 0, color: "#d6f0fb" },
                    { title: "Active", count: total?.active || 0, color: "#d8cdfb" },
                    { title: "Inactive", count: total?.inactive || 0, color: "#e0fbd6" },
                    { title: "% Active", count: total?.percentage, color: "#fbd7d6" },
                ], // optional
                boxWidth: 20,
            },
            // Division	Account Created	Programe
            tableData: {
                head: [
                    [
                        { content: "Practice", styles: { halign: "center", valign: "middle" } },
                        { content: "Referred", styles: { halign: "center", valign: "middle" } },
                        { content: "Onboarded", styles: { halign: "center", valign: "middle" } },
                        { content: "Active", styles: { halign: "center", valign: "middle" } },
                        { content: "Inactive", styles: { halign: "center", valign: "middle" } },
                    ],
                ],
                body: clinicTableData,
                headStyles: {
                    fillColor: [186, 242, 210], // light green like screenshot
                    textColor: "black",
                    halign: "center",
                    valign: "middle",
                },
                styles: {
                    halign: "center",
                    valign: "middle",
                },
            },
            headColor: "#baf2d2",
        },
    ];
}
function makeGapColumn(cellWidth) {
    return {
        content: "",
        styles: {
            fillColor: 0, // transparent
            lineWidth: { top: 0, bottom: 0, left: 0.5, right: 0.5 }, // only verticals
            lineColor: { left: "#555", right: "#555" }
        },
        cellWidth
    };
}


export const makeDownloadPdf = async (data = {}, bgColors = "#f7fafe", type = "referral-report") => {
    const bgColor = "#fff";
    const { referralData, formData, coachingInsight = null } = data;
    const doc = new jsPDF("p", "mm", "a4");
    const pageHeight = doc.internal.pageSize.getHeight();
    let currentY = 20;

    const leftLogo = await loadImageAsBase64("/report/goqii.png");
    const rightLogo = await loadImageAsBase64("/report/modality.png");

    // Draw background + header for first page
    addBackgroundColor(doc, bgColor);
    addHeader(doc, leftLogo, rightLogo);
    const tables = type == "referral-report" ? makeReffralTableData(referralData, formData) : makeDivisonTableData(referralData, formData);

    tables.forEach((item, index) => {
        const estimatedTableHeight =
            ((item?.tableData?.body?.length ?? (item?.isThreeTable ? 5 : 0)) + 1) * 10;

        // Add new page if needed
        if (index > 0 && currentY + 30 + estimatedTableHeight > pageHeight) {
            doc.addPage();
            currentY = 20;
            // Always redraw background + header on new page
            addBackgroundColor(doc, bgColor);
            addHeader(doc, leftLogo, rightLogo);
        }
        const headTextColor = type == "referral-report" ? "#0978be" : "black";
        // 
        if (!item?.isThreeTable) {
            currentY = addHeading(doc, item.heading, currentY);
            currentY = addTable(doc, item.tableData, currentY, { headColor: item.headColor, headTextColor, leftLogo, rightLogo, bgColor });
        } else {
            currentY = makeThreeTable(doc, currentY, coachingInsight, formData);
            currentY += 40;
        }
    });


    doc.save(`${type}.pdf`);
};